import { useApolloClient } from '@apollo/client/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type {
  ApolloGraphqlClient,
  ReportLog,
  ReportStatus
} from '@/utils/reportPolling';
import { pollReportStatus } from '@/utils/reportPolling';

export function useReportPolling(params: {
  enabled?: boolean;
  intervalMs?: number;
  reportId: null | number;
  reportType: 'ewr' | 'loan_tape' | 'pid' | null;
}): { error: null | string; logs: ReportLog[]; status: null | ReportStatus } {
  const client = useApolloClient() as unknown as ApolloGraphqlClient;

  const enabled = params.enabled ?? true;
  const intervalMs = params.intervalMs ?? 3000;

  const reportId = params.reportId ?? null;
  const reportType = params.reportType ?? null;

  const key = useMemo(
    () => `${reportType || 'none'}:${reportId ?? 'none'}`,
    [reportId, reportType]
  );

  const [status, setStatus] = useState<null | ReportStatus>(null);
  const [logs, setLogs] = useState<ReportLog[]>([]);
  const [error, setError] = useState<null | string>(null);

  const intervalRef = useRef<null | ReturnType<typeof setInterval>>(null);

  useEffect(() => {
    // Reset when a new job is passed in
    setStatus(null);
    setLogs([]);
    setError(null);
  }, [key]);

  useEffect(() => {
    if (!enabled) return;
    if (reportId === null || !reportType) return;

    let cancelled = false;

    const stop = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const pollOnce = async () => {
      try {
        const res = await pollReportStatus({ client, reportId, reportType });
        if (cancelled) return;

        setStatus(res.status);
        setLogs(res.logs);
        setError(
          res.status === 'failed'
            ? res.error || 'Report generation failed'
            : null
        );

        if (res.status === 'completed' || res.status === 'failed') {
          stop();
        }
      } catch (e) {
        if (cancelled) return;
        setError(
          e instanceof Error ? e.message : 'Failed to fetch report status'
        );
      }
    };

    void pollOnce();
    intervalRef.current = setInterval(() => {
      void pollOnce();
    }, intervalMs);

    return () => {
      cancelled = true;
      stop();
    };
  }, [client, enabled, intervalMs, reportId, reportType]);

  return { error, logs, status };
}
